import { useEffect, useRef, useState } from "react";
import api, { num, dec, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Trash2, Pencil, Truck, Paperclip, FileText, ImageIcon, X, Upload, Loader2, Package, Tag, CheckCircle2, Barcode, Hash,
} from "lucide-react";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS = {
  forventet: { label: "Forventet", cls: "bg-amber-500/15 text-amber-600 border-amber-500/30" },
  modtaget: { label: "Modtaget", cls: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30" },
};
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const imageUrl = (path) =>
  `${API_BASE}/tradepartner/deliveries/image/${path}?auth=${encodeURIComponent(localStorage.getItem("cf_token") || "")}`;
const isPdf = (name = "") => name.toLowerCase().endsWith(".pdf");
const fmtDate = (s) => {
  if (!s) return "";
  const [y, m, d] = s.split("-");
  return `${parseInt(d, 10)}/${parseInt(m, 10)}-${y}`;
};
const kr = (v) => `${dec(v)} kr`;
const parseNum = (v) => parseFloat(String(v).replace(/\./g, "").replace(",", ".")) || 0;
const emptyForm = () => ({
  date: todayIso(), description: "", store: "Falster", invoice_number: "", barcodes: [],
  qty: "", qty_falster: "", qty_ringsted: "", price: "", status: "forventet",
  image_path: "", image_filename: "", content_type: "",
});

export function TradepartnerDeliveries() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [storeFilter, setStoreFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [localPreview, setLocalPreview] = useState("");
  const [barcodeInput, setBarcodeInput] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/tradepartner/deliveries"); setRows(r.data); }
    catch { toast.error("Kunne ikke hente leveringer"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(emptyForm()); setLocalPreview(""); setBarcodeInput(""); setDialogOpen(true); };
  const openEdit = (d) => {
    setEditing(d);
    setLocalPreview("");
    setBarcodeInput("");
    setForm({
      date: d.date, description: d.description, store: d.store || "Falster",
      invoice_number: d.invoice_number || "", barcodes: d.barcodes || [],
      qty: d.qty ? String(d.qty) : "", qty_falster: d.qty_falster ? String(d.qty_falster) : "", qty_ringsted: d.qty_ringsted ? String(d.qty_ringsted) : "",
      price: d.price ? String(d.price) : "", status: d.status,
      image_path: d.image_path || "", image_filename: d.image_filename || "", content_type: d.content_type || "",
    });
    setDialogOpen(true);
  };

  const addBarcode = () => {
    const v = barcodeInput.trim();
    if (!v) return;
    setForm((f) => (f.barcodes.includes(v) ? f : { ...f, barcodes: [...f.barcodes, v] }));
    setBarcodeInput("");
  };
  const removeBarcode = (code) => setForm((f) => ({ ...f, barcodes: f.barcodes.filter((c) => c !== code) }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const okExt = /\.(jpe?g|png|webp|gif|heic|pdf)$/i.test(file.name);
    if (!okExt && !file.type.startsWith("image/") && file.type !== "application/pdf") {
      toast.error("Kun billeder eller PDF er tilladt"); return;
    }
    if (file.size > 15 * 1024 * 1024) { toast.error("Filen er for stor (max 15 MB)"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/tradepartner/deliveries/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_path: r.data.image_path, image_filename: r.data.image_filename, content_type: r.data.content_type || "" }));
      setLocalPreview(URL.createObjectURL(file));
      toast.success("Fil vedhæftet");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload fejlede");
    } finally { setUploading(false); }
  };
  const removeAttachment = () => { setLocalPreview(""); setForm((f) => ({ ...f, image_path: "", image_filename: "", content_type: "" })); };

  const save = async () => {
    if (!form.date) { toast.error("Vælg en dato"); return; }
    const payload = { ...form, qty: parseNum(form.qty), qty_falster: parseNum(form.qty_falster), qty_ringsted: parseNum(form.qty_ringsted), price: parseNum(form.price) };
    setSaving(true);
    try {
      if (editing) await api.put(`/tradepartner/deliveries/${editing.id}`, payload);
      else await api.post("/tradepartner/deliveries", payload);
      setDialogOpen(false);
      toast.success(editing ? "Levering opdateret" : "Levering oprettet");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Kunne ikke gemme");
    } finally { setSaving(false); }
  };

  const remove = async (d) => {
    if (!window.confirm("Slet denne levering?")) return;
    try { await api.delete(`/tradepartner/deliveries/${d.id}`); toast.success("Levering slettet"); load(); }
    catch { toast.error("Kunne ikke slette"); }
  };

  const markReceived = async (d) => {
    setRows((xs) => xs.map((x) => (x.id === d.id ? { ...x, status: "modtaget" } : x)));
    try {
      await api.put(`/tradepartner/deliveries/${d.id}`, {
        date: d.date, description: d.description, store: d.store || "", invoice_number: d.invoice_number || "", barcodes: d.barcodes || [],
        qty: d.qty, qty_falster: d.qty_falster || 0, qty_ringsted: d.qty_ringsted || 0, price: d.price, status: "modtaget",
        image_path: d.image_path || "", image_filename: d.image_filename || "", content_type: d.content_type || "",
      });
      toast.success("Markeret som modtaget");
    } catch {
      setRows((xs) => xs.map((x) => (x.id === d.id ? { ...x, status: "forventet" } : x)));
      toast.error("Kunne ikke opdatere status");
    }
  };

  const filtered = rows.filter((d) =>
    (statusFilter === "all" || d.status === statusFilter) &&
    (storeFilter === "all" || d.store === storeFilter || (storeFilter !== "all" && d.store === "Begge")));
  const expectedRows = rows.filter((d) => d.status === "forventet");
  const expectedValue = expectedRows.reduce((s, d) => s + (d.value || 0), 0);
  const expectedQty = expectedRows.reduce((s, d) => s + (d.qty || 0), 0);
  const totalValue = rows.reduce((s, d) => s + (d.value || 0), 0);

  const formPrice = parseNum(form.price);
  const formQty = form.store === "Begge" ? parseNum(form.qty_falster) + parseNum(form.qty_ringsted) : parseNum(form.qty);

  return (
    <div className="max-w-4xl space-y-5" data-testid="tradepartner-deliveries">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
        <div>
          <h4 className="font-heading text-xl font-light text-slate-900 flex items-center gap-2">
            <Truck className="w-5 h-5 text-indigo-500" /> Leveringsoversigt
          </h4>
          <p className="text-sm text-slate-500">Notér hvad der er på vej — antal, pris pr. stk og billede.</p>
        </div>
        <Button onClick={openNew} className="rounded-lg bg-indigo-500 text-white hover:bg-indigo-600 gap-2 self-start sm:self-auto" data-testid="tp-delivery-new-btn">
          <Plus className="w-4 h-4" /> Ny levering
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4" data-testid="tp-total-expected-value">
          <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500">Forventet værdi i alt</p>
          <p className="text-2xl font-bold tabular-nums text-slate-900 mt-1">{kr(expectedValue)}</p>
          <p className="text-xs text-slate-500 mt-0.5">{expectedRows.length} leveringer · {num(expectedQty)} stk</p>
        </div>
        <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4" data-testid="tp-total-all-value">
          <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500">Samlet værdi (alle)</p>
          <p className="text-2xl font-bold tabular-nums text-slate-900 mt-1">{kr(totalValue)}</p>
          <p className="text-xs text-slate-500 mt-0.5">{rows.length} leveringer i alt</p>
        </div>
        <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4">
          <p className="text-[11px] uppercase tracking-[0.14em] text-slate-500">Modtaget</p>
          <p className="text-2xl font-bold tabular-nums text-slate-900 mt-1">{rows.filter((d) => d.status === "modtaget").length}</p>
          <p className="text-xs text-slate-500 mt-0.5">leveringer modtaget</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {[["all", "Alle"], ["forventet", "Forventet"], ["modtaget", "Modtaget"]].map(([id, label]) => (
          <button key={id} onClick={() => setStatusFilter(id)} data-testid={`tp-delivery-filter-${id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${statusFilter === id ? "bg-indigo-500 text-white" : "bg-[#ffffff] border border-[#e2e8f0] text-slate-500 hover:text-slate-900"}`}>
            {label}
          </button>
        ))}
        <span className="w-px h-5 bg-[#e2e8f0] mx-1" />
        {[["all", "Alle butikker"], ["Falster", "Falster"], ["Ringsted", "Ringsted"]].map(([id, label]) => (
          <button key={id} onClick={() => setStoreFilter(id)} data-testid={`tp-store-filter-${id}`}
            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${storeFilter === id ? "bg-sky-500 text-white" : "bg-[#ffffff] border border-[#e2e8f0] text-slate-500 hover:text-slate-900"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {loading && <p className="text-sm text-slate-500 py-6" data-testid="tp-deliveries-loading">Henter leveringer…</p>}
        {!loading && filtered.map((d) => {
          const S = STATUS[d.status] || STATUS.forventet;
          return (
            <div key={d.id} className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] p-4 flex flex-col sm:flex-row gap-4" data-testid={`tp-delivery-${d.id}`}>
              {d.image_path && (
                <button onClick={() => setPreview({ path: d.image_path, filename: d.image_filename })}
                  className="shrink-0 w-full sm:w-24 h-24 rounded-lg overflow-hidden bg-[#f8fafc] border border-[#e2e8f0] flex items-center justify-center hover:border-indigo-400 transition-colors"
                  data-testid={`tp-delivery-thumb-${d.id}`}>
                  {isPdf(d.image_filename)
                    ? <FileText className="w-8 h-8 text-slate-500" />
                    : <img src={imageUrl(d.image_path)} alt={d.image_filename} className="w-full h-full object-cover" />}
                </button>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium border ${S.cls}`}>{S.label}</span>
                  {d.store && <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-sky-500/15 text-sky-600 border border-sky-500/30">{d.store}</span>}
                  <span className="text-xs text-slate-500">{fmtDate(d.date)}</span>
                </div>
                {d.description && <p className="font-medium text-slate-800 mt-1.5 whitespace-pre-wrap break-words">{d.description}</p>}
                <div className="flex items-center gap-4 flex-wrap mt-1 text-sm text-slate-600">
                  <span className="inline-flex items-center gap-1.5"><Package className="w-4 h-4 text-slate-400" />{num(d.qty)} stk</span>
                  <span className="inline-flex items-center gap-1.5"><Tag className="w-4 h-4 text-slate-400" />{kr(d.price)}/stk</span>
                  <span className="font-semibold text-slate-900 tabular-nums">= {kr(d.value)}</span>
                </div>
                {d.store === "Begge" && ((d.qty_falster || 0) > 0 || (d.qty_ringsted || 0) > 0) && (
                  <p className="text-xs text-slate-500 mt-1" data-testid={`tp-delivery-breakdown-${d.id}`}>
                    Falster {num(d.qty_falster)} stk · Ringsted {num(d.qty_ringsted)} stk
                  </p>
                )}
                {(d.invoice_number || (d.barcodes && d.barcodes.length > 0)) && (
                  <div className="flex items-center gap-2 flex-wrap mt-1.5">
                    {d.invoice_number && (
                      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 border border-[#e2e8f0] text-slate-600" data-testid={`tp-delivery-invoice-${d.id}`}>
                        <Hash className="w-3 h-3" />Faktura {d.invoice_number}
                      </span>
                    )}
                    {(d.barcodes || []).map((c) => (
                      <span key={c} className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-slate-100 border border-[#e2e8f0] text-slate-600">
                        <Barcode className="w-3 h-3" />{c}
                      </span>
                    ))}
                  </div>
                )}
                {d.image_filename && (
                  <p className="text-xs text-slate-500 mt-1.5 flex items-center gap-1"><Paperclip className="w-3 h-3" /> {d.image_filename}</p>
                )}
                {d.status === "forventet" && (
                  <Button onClick={() => markReceived(d)} size="sm" data-testid={`tp-delivery-receive-${d.id}`}
                    className="mt-2 h-8 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 gap-1.5">
                    <CheckCircle2 className="w-4 h-4" /> Markér modtaget
                  </Button>
                )}
              </div>
              <div className="flex sm:flex-col gap-1 shrink-0">
                <Button size="icon" variant="ghost" onClick={() => openEdit(d)}
                  className="h-8 w-8 text-slate-500 hover:text-indigo-600" data-testid={`tp-delivery-edit-${d.id}`}><Pencil className="w-4 h-4" /></Button>
                <Button size="icon" variant="ghost" onClick={() => remove(d)}
                  className="h-8 w-8 text-slate-500 hover:text-red-600" data-testid={`tp-delivery-delete-${d.id}`}><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          );
        })}
        {!loading && filtered.length === 0 && (
          <div className="rounded-2xl bg-[#ffffff] border border-[#e2e8f0] px-4 py-12 text-center text-slate-500" data-testid="tp-deliveries-empty">
            Ingen leveringer endnu. Klik "Ny levering" for at notere hvad der kommer.
          </div>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-800 max-w-lg" data-testid="tp-delivery-dialog">
          <DialogHeader><DialogTitle className="font-heading font-light">{editing ? "Redigér levering" : "Ny levering"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Dato</Label>
                <Input type="date" value={form.date} onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))}
                  className="mt-1 bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="tp-delivery-date-input" />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Butik</Label>
                <Select value={form.store} onValueChange={(v) => setForm((f) => ({ ...f, store: v }))}>
                  <SelectTrigger className="mt-1 bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="tp-delivery-store-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-800">
                    <SelectItem value="Falster">Falster</SelectItem>
                    <SelectItem value="Ringsted">Ringsted</SelectItem>
                    <SelectItem value="Begge">Begge</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger className="mt-1 bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="tp-delivery-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-800">
                    <SelectItem value="forventet">Forventet</SelectItem>
                    <SelectItem value="modtaget">Modtaget</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Beskrivelse (hvad kommer der?)</Label>
              <textarea value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                rows={3} placeholder="Skriv varerne…"
                className="mt-1 w-full rounded-md bg-[#f8fafc] border border-[#e2e8f0] text-slate-700 text-sm p-2.5 resize-y focus:outline-none focus:ring-1 focus:ring-indigo-400"
                data-testid="tp-delivery-description-input" />
            </div>
            {form.store === "Begge" ? (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Antal Falster</Label>
                  <Input inputMode="decimal" value={form.qty_falster} onChange={(e) => setForm((f) => ({ ...f, qty_falster: e.target.value }))}
                    placeholder="fx 60" className="mt-1 bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="tp-delivery-qty-falster-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Antal Ringsted</Label>
                  <Input inputMode="decimal" value={form.qty_ringsted} onChange={(e) => setForm((f) => ({ ...f, qty_ringsted: e.target.value }))}
                    placeholder="fx 40" className="mt-1 bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="tp-delivery-qty-ringsted-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Pris pr. stk (kr)</Label>
                  <Input inputMode="decimal" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                    placeholder="fx 12,50" className="mt-1 bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="tp-delivery-price-input" />
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-slate-500">Antal (stk)</Label>
                  <Input inputMode="decimal" value={form.qty} onChange={(e) => setForm((f) => ({ ...f, qty: e.target.value }))}
                    placeholder="fx 100" className="mt-1 bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="tp-delivery-qty-input" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Pris pr. stk (kr)</Label>
                  <Input inputMode="decimal" value={form.price} onChange={(e) => setForm((f) => ({ ...f, price: e.target.value }))}
                    placeholder="fx 12,50" className="mt-1 bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="tp-delivery-price-input" />
                </div>
              </div>
            )}
            {form.store === "Begge" && (formQty > 0) && (
              <p className="text-xs text-slate-500" data-testid="tp-delivery-qty-total">I alt {num(formQty)} stk (Falster {num(parseNum(form.qty_falster))} · Ringsted {num(parseNum(form.qty_ringsted))})</p>
            )}
            {(formQty > 0 && formPrice > 0) && (
              <div className="rounded-lg bg-indigo-50 border border-indigo-100 px-3 py-2 text-sm text-indigo-700" data-testid="tp-delivery-value-preview">
                Samlet værdi: <b className="tabular-nums">{kr(formQty * formPrice)}</b> ({num(formQty)} × {kr(formPrice)})
              </div>
            )}
            <div>
              <Label className="text-xs text-slate-500">Fakturanummer (valgfrit)</Label>
              <Input value={form.invoice_number} onChange={(e) => setForm((f) => ({ ...f, invoice_number: e.target.value }))}
                placeholder="fx 100482" className="mt-1 bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="tp-delivery-invoice-input" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Stregkoder (valgfrit)</Label>
              <div className="mt-1 flex gap-2">
                <Input value={barcodeInput} onChange={(e) => setBarcodeInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addBarcode(); } }}
                  placeholder="Scan eller skriv en stregkode, tryk Tilføj"
                  className="bg-[#f8fafc] border-[#e2e8f0] text-slate-700" data-testid="tp-delivery-barcode-input" />
                <Button type="button" variant="outline" onClick={addBarcode}
                  className="rounded-lg bg-[#f8fafc] border-[#e2e8f0] text-slate-700 hover:bg-[#eef2f6] gap-1.5 shrink-0" data-testid="tp-delivery-barcode-add-btn">
                  <Plus className="w-4 h-4" /> Tilføj
                </Button>
              </div>
              {form.barcodes.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2" data-testid="tp-delivery-barcode-chips">
                  {form.barcodes.map((c) => (
                    <span key={c} className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-full text-xs font-medium bg-slate-100 border border-[#e2e8f0] text-slate-600">
                      <Barcode className="w-3 h-3" />{c}
                      <button type="button" onClick={() => removeBarcode(c)} className="ml-0.5 text-slate-400 hover:text-red-600" data-testid={`tp-delivery-barcode-remove-${c}`}><X className="w-3 h-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs text-slate-500">Billede/PDF (valgfrit)</Label>
              <input ref={fileRef} type="file" accept="image/*,.pdf,.heic" onChange={handleFile} className="hidden" data-testid="tp-delivery-file-input" />
              {form.image_path ? (
                <div className="mt-1 flex items-center gap-3 rounded-lg bg-[#f8fafc] border border-[#e2e8f0] p-2.5">
                  {isPdf(form.image_filename)
                    ? <FileText className="w-9 h-9 text-slate-500 shrink-0" />
                    : <img src={localPreview || imageUrl(form.image_path)} alt="preview" className="w-12 h-12 rounded object-cover shrink-0" />}
                  <span className="flex-1 text-sm text-slate-500 truncate">{form.image_filename}</span>
                  <Button size="icon" variant="ghost" onClick={removeAttachment} className="h-8 w-8 text-slate-500 hover:text-red-600 shrink-0" data-testid="tp-delivery-remove-file-btn"><X className="w-4 h-4" /></Button>
                </div>
              ) : (
                <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading}
                  className="mt-1 w-full rounded-lg bg-[#f8fafc] border-[#e2e8f0] border-dashed text-slate-500 hover:bg-[#eef2f6] hover:text-slate-900 gap-2 h-14" data-testid="tp-delivery-upload-btn">
                  {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploader…</> : <><Upload className="w-4 h-4" /> Vedhæft billede eller PDF</>}
                </Button>
              )}
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setDialogOpen(false)} className="text-slate-500 hover:text-slate-900" data-testid="tp-delivery-cancel-btn">Annullér</Button>
            <Button onClick={save} disabled={saving || uploading} className="rounded-lg bg-indigo-500 text-white hover:bg-indigo-600 gap-2" data-testid="tp-delivery-save-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} {editing ? "Gem" : "Opret"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="bg-[#ffffff] border-[#e2e8f0] text-slate-800 max-w-3xl" data-testid="tp-delivery-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-light flex items-center gap-2 text-base">
              {preview && isPdf(preview.filename) ? <FileText className="w-4 h-4" /> : <ImageIcon className="w-4 h-4" />}
              {preview?.filename}
            </DialogTitle>
          </DialogHeader>
          {preview && (
            isPdf(preview.filename)
              ? <iframe src={imageUrl(preview.path)} title={preview.filename} className="w-full h-[70vh] rounded-lg bg-white" />
              : <img src={imageUrl(preview.path)} alt={preview.filename} className="w-full max-h-[70vh] object-contain rounded-lg" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
